import { createFileRoute } from "@tanstack/react-router";
import { motion } from "motion/react";
import { HelpCircle } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { breadcrumbSchema, faqPageSchema, localizedHead, validateLangSearch } from "@/lib/seo";
import { RelatedLinks } from "@/components/related-links";
import { ConsultCta } from "@/components/consult-cta";

interface QA {
  q: string;
  a: string;
}

const faqs: Record<"de" | "en", QA[]> = {
  de: [
    {
      q: "Was ist Retatrutid (LY3437943)?",
      a: "Retatrutid ist ein experimenteller Wirkstoff von Eli Lilly und der erste Triple-Agonist, der gleichzeitig an den GLP-1-, GIP- und Glucagon-Rezeptoren wirkt. Er befindet sich aktuell in Phase-III-Studien (TRIUMPH-Programm) zur Behandlung von Adipositas und Typ-2-Diabetes.",
    },
    {
      q: "Wann kommt die Zulassung von Retatrutid?",
      a: "Eine reguläre Zulassung durch FDA und EMA wird frühestens für Ende 2026 oder 2027 erwartet, abhängig vom Abschluss der TRIUMPH-1- und TRIUMPH-2-Studien. Bis dahin ist Retatrutid ausschließlich innerhalb klinischer Studien verfügbar – inklusive des genehmigten Programms unserer Partner-Klinik.",
    },
    {
      q: "Wie viel Gewicht verliert man mit Retatrutid?",
      a: "In Phase-II-Daten wurde in der Höchstdosis (12 mg wöchentlich) nach 48 Wochen ein durchschnittlicher Gewichtsverlust von rund 24 % des Körpergewichts erreicht – deutlich mehr als bei Semaglutid (~15 %) und Tirzepatid (~20–22 %).",
    },
    {
      q: "Retatrutid vs Semaglutid vs Tirzepatid – was ist der Unterschied?",
      a: "Semaglutid (Ozempic, Wegovy) ist ein Mono-Agonist am GLP-1-Rezeptor. Tirzepatid (Mounjaro, Zepbound) ist ein Dual-Agonist an GLP-1 und GIP. Retatrutid setzt zusätzlich am Glucagon-Rezeptor an und steigert dadurch den Grundumsatz – ein Mechanismus, den die anderen beiden nicht haben.",
    },
    {
      q: "Welche Nebenwirkungen hat Retatrutid?",
      a: "Die häufigsten Nebenwirkungen sind gastrointestinal: Übelkeit, Erbrechen, Durchfall und Verstopfung, meist zu Beginn der Therapie und dosisabhängig. In den Studien wurden zudem leichte Herzfrequenz-Anstiege beobachtet. Langzeitdaten stehen noch aus.",
    },
    {
      q: "Kann man Retatrutid schon erhalten?",
      a: 'Retatrutid ist als Fertigarzneimittel nirgends frei erhältlich. Ein sicherer Zugang ist über genehmigte klinische Studienprogramme – wie das unserer Partner-Klinik – möglich. Online angebotene „Research Peptides" stammen aus unregulierten Quellen und sind nicht Teil eines genehmigten Studienprogramms; ihre Reinheit, Dosierung und Sterilität sind unbekannt und die Anwendung birgt erhebliche Gesundheitsrisiken.',
    },
    {
      q: "Was ist TRIUMPH-1?",
      a: "TRIUMPH-1 ist die zentrale Phase-III-Zulassungsstudie zu Retatrutid bei Erwachsenen mit Adipositas. Sie untersucht Sicherheit und Wirksamkeit über 80 Wochen im Vergleich zu Placebo. Die Ergebnisse werden 2026 als Grundlage für den Zulassungsantrag erwartet.",
    },
    {
      q: "Wie funktioniert der Triple-Agonismus?",
      a: "Der GLP-1-Rezeptor steuert Sättigung im Gehirn und verzögert die Magenentleerung. Der GIP-Rezeptor verbessert die Insulinsensitivität und den Fettstoffwechsel. Der Glucagon-Rezeptor erhöht direkt den Energieverbrauch in Ruhe. Zusammen wirken die drei Signalwege synergetisch.",
    },
  ],
  en: [
    {
      q: "What is Retatrutide (LY3437943)?",
      a: "Retatrutide is a triple-agonist compound from Eli Lilly and the first to act simultaneously on the GLP-1, GIP and glucagon receptors. It is currently in Phase III trials (TRIUMPH program) for obesity and type-2 diabetes, and available through approved clinical study programs for eligible participants.",
    },
    {
      q: "When will Retatrutide be approved?",
      a: "Regular FDA and EMA approval is expected at the earliest by late 2026 or 2027, depending on completion of the TRIUMPH-1 and TRIUMPH-2 trials. Until then, Retatrutide is only available inside clinical studies, including our partner clinic's approved program.",
    },
    {
      q: "How much weight loss with Retatrutide?",
      a: "In Phase II data at the highest dose (12 mg weekly) an average weight loss of about 24% of body weight was achieved after 48 weeks — substantially more than Semaglutide (~15%) and Tirzepatide (~20–22%).",
    },
    {
      q: "Retatrutide vs Semaglutide vs Tirzepatide — what's the difference?",
      a: "Semaglutide (Ozempic, Wegovy) is a mono-agonist at the GLP-1 receptor. Tirzepatide (Mounjaro, Zepbound) is a dual-agonist at GLP-1 and GIP. Retatrutide additionally targets the glucagon receptor, thereby increasing basal metabolic rate — a mechanism the other two lack.",
    },
    {
      q: "What are the side effects of Retatrutide?",
      a: "The most common side effects are gastrointestinal: nausea, vomiting, diarrhea and constipation, mostly at treatment start and dose-dependent. Trials also observed mild heart-rate increases. Long-term safety data are still pending.",
    },
    {
      q: "Can you get Retatrutide already?",
      a: "Retatrutide is not available as a finished pharmaceutical over the counter. Safe access is possible through approved clinical study programs, such as our partner clinic's. 'Research peptides' offered online are unregulated and not part of an approved study program — purity, dosage and sterility are unknown and use carries substantial health risks.",

    },
    {
      q: "What is TRIUMPH-1?",
      a: "TRIUMPH-1 is the pivotal Phase III registration trial of Retatrutide in adults with obesity. It evaluates safety and efficacy over 80 weeks versus placebo. Results are expected in 2026 as the basis for the regulatory submission.",
    },
    {
      q: "How does triple-agonism work?",
      a: "The GLP-1 receptor drives brain satiety and slows gastric emptying. The GIP receptor improves insulin sensitivity and lipid handling. The glucagon receptor directly raises resting energy expenditure. The three pathways act synergistically.",
    },
  ],
};

export const Route = createFileRoute("/faq")({
  validateSearch: validateLangSearch,
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  loader: ({ deps }) => ({ lang: deps.lang }),
  head: ({ loaderData }) => {
    const base = localizedHead("/faq", loaderData?.lang);
    const lang = loaderData?.lang ?? "en";
    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema("/faq", loaderData?.lang)) },
        { type: "application/ld+json", children: JSON.stringify(faqPageSchema("/faq", loaderData?.lang, faqs[lang])) },
      ],
    };
  },

  component: FaqPage,
});


function FaqPage() {
  const { lang } = useI18n();
  const items = faqs[lang];
  const heading =
    lang === "de"
      ? { eyebrow: "Häufige Fragen", h1: "Retatrutid – FAQ", lead: "Die häufigsten Fragen zu Wirkung, Studien, Zulassung, Sicherheit und Vergleich mit Semaglutid und Tirzepatid – kompakt beantwortet." }
      : { eyebrow: "Frequently asked", h1: "Retatrutide – FAQ", lead: "The most common questions on mechanism, studies, approval, safety and comparison with Semaglutide and Tirzepatide — answered concisely." };

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 md:py-20">
      <div className="max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo">
          {heading.eyebrow}
        </p>
        <h1 className="mt-3 font-display text-4xl leading-tight text-foreground md:text-5xl">
          {heading.h1}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">{heading.lead}</p>
      </div>

      <div className="mt-10 space-y-4">
        {items.map((f, i) => (
          <motion.details
            key={f.q}
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.35, delay: i * 0.03 }}
            className="group card-elevated overflow-hidden p-0"
          >
            <summary className="flex cursor-pointer list-none items-start justify-between gap-4 p-5 text-left">
              <div className="flex items-start gap-3">
                <HelpCircle className="mt-0.5 h-5 w-5 flex-none text-indigo" />
                <h2 className="text-base font-semibold text-foreground md:text-lg">{f.q}</h2>
              </div>
              <span
                aria-hidden
                className="mt-1 grid h-6 w-6 flex-none place-items-center rounded-full border border-border text-sm text-muted-foreground transition-transform group-open:rotate-45"
              >
                +
              </span>
            </summary>
            <div className="border-t border-border/60 px-5 py-4 text-sm leading-relaxed text-muted-foreground">
              {f.a}
            </div>
          </motion.details>
        ))}
      </div>

      <ConsultCta variant="section" />
      <RelatedLinks only={["/vergleich", "/zulassung", "/sicherheit"]} />
    </div>
  );
}
